import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState, useSyncExternalStore, type FormEvent } from "react";
import { AuthFrame } from "@/components/marketing/auth-frame";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PinSetupDialog } from "@/components/app/shell/pin-setup-dialog";
import {
  needsSecondFactor,
  refusalWords,
  secondFactorDestination,
  sendSecondFactorCode,
  signIn,
  signInWithPin,
} from "@/lib/host/auth-client";
import { SecondFactorStep } from "./-components/second-factor-step";
import { insidePhoneApp, writeLastActivity } from "@/lib/session/idle-timeout";
import { loginNotice } from "./-login-notice";
import { landingAfterSignIn } from "./-login-landing";
import { loginSearch } from "./-login-search";
import { useSessionAtOpen } from "./-session-at-open";
import { orpc } from "@/lib/orpc";
import { beginSession } from "@/lib/session/session";
import { PinLoginForm } from "./-components/pin-login-form";
import { PasswordLoginForm } from "./-components/password-login-form";
import { AUDIENCE_WORDS } from "./-login-audience";

export const Route = createFileRoute("/login")({
  validateSearch: loginSearch,
  component: LoginPage,
});

const never = () => () => {};

/* Better Auth's codes for a pair that does not match. Only these mark the two
   fields invalid; anything else (rate limit, network) is not the fields' fault. */
const WRONG_PAIR = new Set(["INVALID_EMAIL_OR_PASSWORD", "INVALID_USERNAME_OR_PASSWORD"]);
const OFFLINE = "Couldn't sign in. Check your connection and try again.";

function LoginPage() {
  const { next, reason, email, after, fresh, as: audience = "staff" } = Route.useSearch();
  const words = AUDIENCE_WORDS[audience];
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState(email ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldsInvalid, setFieldsInvalid] = useState(false);
  const identifierRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  /* Offered only where it will open. `loginContext` is readable without a session and says only
   * whether the caller's address is on some building's allowlist; the API applies the same rule to
   * sign-in, so the form never shows one the endpoint would refuse. */
  const context = useQuery(orpc.auth.loginContext.queryOptions());
  const pinAvailable = context.data?.pinLoginAvailable ?? false;
  const [usePin, setUsePin] = useState(false);
  const [code, setCode] = useState("");
  const queryClient = useQueryClient();
  const [offerPin, setOfferPin] = useState(false);
  // The password passed and the code texted to their phone is what remains.
  const [secondFactor, setSecondFactor] = useState(false);
  // When that first code could not be asked for; the step offers to send another.
  const [sendError, setSendError] = useState<string | null>(null);
  // Which phone or inbox the code went to, when the server can say.
  const [destination, setDestination] = useState<Awaited<
    ReturnType<typeof secondFactorDestination>
  > | null>(null);
  // The phone app has no idle sign-out; false on the server so hydration matches.
  const inApp = useSyncExternalStore(never, insidePhoneApp, () => false);
  /* Somebody already signed in (a bookmark, the phone app reopening) goes straight on. Only a
     check made after this page opened counts: a cached answer could be a session just ended. */
  const me = useQuery({
    ...orpc.auth.me.queryOptions(),
    retry: false,
    refetchOnMount: "always",
  });
  // A refused check keeps the last answer's data, which is somebody signed out: success only.
  const signedIn = me.isFetchedAfterMount && me.isSuccess && Boolean(me.data);
  useSessionAtOpen({
    fresh: fresh === true,
    answered: me.isFetchedAfterMount,
    signedIn,
    busy: pending || offerPin,
    goOn: () =>
      void landingAfterSignIn(queryClient, next).then((to) => navigate({ to, replace: true })),
    queryClient,
  });

  const onPinSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (code.length !== 6) {
      setError("Enter all 6 digits of your code.");
      codeRef.current?.focus();
      return;
    }
    setError(null);
    setPending(true);
    let result: Awaited<ReturnType<typeof signInWithPin>>;
    try {
      result = await signInWithPin(code);
    } catch {
      setError(OFFLINE);
      return;
    } finally {
      setPending(false);
    }
    if (result.error) {
      setError(result.error.message);
      setCode("");
      codeRef.current?.focus();
      return;
    }
    writeLastActivity(Date.now());
    beginSession(queryClient);
    await navigate({ to: await landingAfterSignIn(queryClient, next), replace: true });
  };

  /** Whether they set a code or waved it away, the destination is the same. */
  const finishSignIn = async () => {
    setOfferPin(false);
    writeLastActivity(Date.now());
    beginSession(queryClient);
    await navigate({ to: await landingAfterSignIn(queryClient, next), replace: true });
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    setFieldsInvalid(false);
    let result: Awaited<ReturnType<typeof signIn>>;
    try {
      result = await signIn(identifier.trim(), password);
    } catch {
      setError(OFFLINE);
      return;
    } finally {
      setPending(false);
    }
    if (result.error) {
      const wrongPair = WRONG_PAIR.has(result.error.code ?? "");
      setFieldsInvalid(wrongPair);
      setError(
        wrongPair
          ? "That email or username and password don't match. Check both and try again, or reset your password."
          : refusalWords(result.error, OFFLINE),
      );
      if (wrongPair) identifierRef.current?.focus();
      return;
    }
    if (needsSecondFactor(result)) {
      // The code goes out now, so the next screen is already waiting on it.
      const [sent, where] = await Promise.all([
        sendSecondFactorCode().catch(() => ({ error: { message: OFFLINE } })),
        secondFactorDestination(),
      ]);
      setSendError(sent.error?.message ?? null);
      setDestination(where);
      setSecondFactor(true);
      return;
    }
    /* Offer the quick code here, not in a settings page nobody visits. Somebody who has just typed
     * a password at a shared station is exactly the person who wants a six-digit one, and this is
     * the only moment their password is already in hand — so they are not asked for it twice. */
    const pin = await queryClient.fetchQuery(orpc.auth.pinStatus.queryOptions()).catch(() => null);
    if (pin?.eligible && pin.status !== "active") {
      setOfferPin(true);
      return;
    }
    await finishSignIn();
  };

  const notice = loginNotice({
    secondFactor: secondFactor ? (destination?.channel ?? "sms") : null,
    reason,
    after,
    usePin,
  });

  const description = notice ?? words.hint;

  return (
    <AuthFrame footer={inApp ? undefined : "Shared computer? You're signed out when it sits idle."}>
      <CardHeader className="items-center text-center">
        {/* The wordmark sits above the card (AuthFrame). A login page with no
            mark does not tell somebody which system they are signing into,
            which matters most to the person who has three of them open. */}
        <CardTitle asChild className="text-xl">
          <h1>Sign in</h1>
        </CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {secondFactor ? (
          <SecondFactorStep
            destination={destination}
            initialError={sendError}
            onDone={finishSignIn}
            onCancel={() => {
              setSecondFactor(false);
              setSendError(null);
              setDestination(null);
              setPassword("");
              setError(null);
            }}
          />
        ) : usePin ? (
          <PinLoginForm
            code={code}
            onCode={setCode}
            codeRef={codeRef}
            error={error}
            pending={pending}
            onSubmit={(event) => void onPinSubmit(event)}
            onBack={() => {
              setUsePin(false);
              setError(null);
            }}
          />
        ) : (
          <PasswordLoginForm
            identifier={identifier}
            onIdentifier={setIdentifier}
            password={password}
            onPassword={setPassword}
            identifierRef={identifierRef}
            fieldsInvalid={fieldsInvalid}
            error={error}
            pending={pending}
            showPassword={showPassword}
            onToggleShow={() => setShowPassword((shown) => !shown)}
            onSubmit={(event) => void onSubmit(event)}
            pinAvailable={pinAvailable}
            help={words.help}
            onUsePin={() => {
              setUsePin(true);
              setError(null);
            }}
          />
        )}
      </CardContent>
      {/* Mounted only when it opens. Rendered always, `useState(initialPassword)`
          would capture the password box while it was still empty and the Save
          button could never enable. */}
      {offerPin ? (
        <PinSetupDialog
          open
          onOpenChange={(nowOpen) => {
            if (!nowOpen) void finishSignIn();
          }}
          initialPassword={password}
          onDone={() => void finishSignIn()}
        />
      ) : null}
    </AuthFrame>
  );
}
